<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/apiClient.js";
import SearchableInput from "@/components/SearchableInput.vue";
import { useBreadcrumbStore } from "@/stores/BreadcrumbStore";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/vue/24/solid";

const route = useRoute();
const router = useRouter();
const breadcrumbStore = useBreadcrumbStore();
const sourceId = computed(() => Number(route.params.id));

const preview = ref(null);
const destination = ref(null);
const destinationQuery = ref("");
const loading = ref(false);
const previewLoading = ref(false);
const merging = ref(false);
const errorMessage = ref("");
const confirmationText = ref("");
const acknowledged = ref(false);

const source = computed(() => preview.value?.source ?? null);
const counts = computed(
  () =>
    preview.value?.counts ?? {
      references: 0,
      quotes: 0,
      purchase_orders: 0,
      contacts: 0,
      addresses: 0,
    },
);
const assets = computed(
  () =>
    preview.value?.assets ?? {
      references: [],
      quotes: [],
      purchase_orders: [],
      contacts: [],
      addresses: [],
    },
);
const totalAssets = computed(
  () =>
    Number(counts.value.references ?? 0) +
    Number(counts.value.quotes ?? 0) +
    Number(counts.value.purchase_orders ?? 0) +
    Number(counts.value.contacts ?? 0) +
    Number(counts.value.addresses ?? 0),
);
const canMerge = computed(
  () =>
    Boolean(destination.value?.id) &&
    acknowledged.value &&
    confirmationText.value === "MERGE" &&
    !merging.value,
);

const customerLabel = (customer) => {
  const contact = customer?.contact?.name ? ` - ${customer.contact.name}` : "";
  const email = customer?.contact?.email ? ` [${customer.contact.email}]` : "";
  return `${customer?.company ?? "Customer"}${contact}${email}`;
};

const loadPreview = async (destinationCustomerId = null) => {
  errorMessage.value = "";
  previewLoading.value = true;

  try {
    const response = await apiClient.get(
      `/api/customer/${sourceId.value}/merge-preview`,
      {
        params: destinationCustomerId
          ? { destination_customer_id: destinationCustomerId }
          : {},
        suppressAutoReload: true,
      },
    );
    preview.value = response.data;
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.message ?? "Unable to load merge details.";
  } finally {
    previewLoading.value = false;
  }
};

const selectDestination = async (customer) => {
  if (!customer?.id) {
    return;
  }

  if (Number(customer.id) === sourceId.value) {
    destination.value = null;
    destinationQuery.value = "";
    errorMessage.value = "Choose a different destination customer.";
    return;
  }

  destination.value = customer;
  destinationQuery.value = customer.company ?? "";
  acknowledged.value = false;
  confirmationText.value = "";
  await loadPreview(customer.id);
};

const mergeCustomers = async () => {
  if (!canMerge.value) {
    return;
  }

  const confirmed = window.confirm(
    `Merge ${source.value?.company ?? "this customer"} into ${destination.value.company}? The source customer will be deleted.`,
  );

  if (!confirmed) {
    return;
  }

  errorMessage.value = "";
  merging.value = true;

  try {
    await apiClient.post(
      `/api/customer/${sourceId.value}/merge`,
      {
        destination_customer_id: destination.value.id,
        confirmation: "MERGE",
        confirm_source_customer_id: sourceId.value,
      },
      {
        suppressAutoReload: true,
      },
    );

    router.push({
      name: "customer-view",
      params: { id: destination.value.id },
      query: { merged_source_id: sourceId.value },
    });
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.message ?? "Unable to merge customers.";
  } finally {
    merging.value = false;
  }
};

watch(source, (value) => {
  if (value?.company) {
    breadcrumbStore.setValue(`Merge ${value.company}`);
  }
});

onMounted(async () => {
  loading.value = true;
  await loadPreview();
  loading.value = false;
});
</script>

<template>
  <div>
    <div class="mb-4 flex justify-end gap-2">
      <router-link
        v-if="source"
        :to="{ name: 'customer-view', params: { id: source.id } }"
        class="btn"
      >
        Back to Customer
      </router-link>
      <router-link to="/customer" class="btn"> Back to Customers </router-link>
    </div>

    <form-box header="Merge Customer">
      <div v-if="loading" class="py-8 text-sm text-slate-500">
        Loading customer merge details...
      </div>

      <div v-else class="space-y-5">
        <div
          v-if="errorMessage"
          class="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-700 dark:bg-rose-950/30 dark:text-rose-200"
        >
          {{ errorMessage }}
        </div>

        <div
          class="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100"
        >
          <ExclamationTriangleIcon class="mt-0.5 h-5 w-5 shrink-0" />
          <p>
            The source customer is fixed on this page. Merging will move its
            references, quotes, purchase orders, contacts, and addresses to the
            destination customer, then delete the source customer.
          </p>
        </div>

        <div
          class="grid gap-5 md:grid-cols-2 md:divide-x md:divide-slate-200 dark:md:divide-slate-700"
        >
          <section class="md:pr-5">
            <label>Source Customer</label>
            <div
              class="text-lg font-semibold text-slate-900 dark:text-slate-100"
            >
              {{ source?.company }}
            </div>
            <div class="mt-2 text-sm text-slate-500 dark:text-slate-400">
              <div v-if="source?.contact">
                {{ source.contact.name }}
                {{ source.contact.email ? `- ${source.contact.email}` : "" }}
              </div>
              <div v-if="source?.website">{{ source.website }}</div>
              <div>ID {{ source?.id }}</div>
            </div>
          </section>

          <section class="md:pl-5">
            <label>Destination Customer</label>
            <SearchableInput
              v-model="destinationQuery"
              url="/api/customer/search?query="
              placeholder="Search destination customer..."
              :output="customerLabel"
              :clearOnSelect="false"
              :autoShowResults="false"
              @selected="selectDestination"
            />
            <div
              v-if="destination"
              class="mt-3 flex items-start gap-2 text-sm text-emerald-700 dark:text-emerald-300"
            >
              <CheckCircleIcon class="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <div class="font-semibold">{{ destination.company }}</div>
                <div
                  v-if="destination.contact?.name"
                  class="text-slate-500 dark:text-slate-400"
                >
                  {{ destination.contact.name }}
                  {{
                    destination.contact.email
                      ? `- ${destination.contact.email}`
                      : ""
                  }}
                </div>
                <div class="text-slate-500 dark:text-slate-400">
                  ID {{ destination.id }}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </form-box>

    <form-box header="Assets to Move">
      <div v-if="previewLoading" class="py-6 text-sm text-slate-500">
        Refreshing asset list...
      </div>

      <div v-else class="space-y-5">
        <div class="grid grid-cols-2 gap-3 text-sm md:grid-cols-6">
          <div class="border-b border-slate-200 pb-2 dark:border-slate-700">
            <div class="text-xs uppercase text-slate-500">Total</div>
            <div class="text-lg font-semibold">{{ totalAssets }}</div>
          </div>
          <div class="border-b border-slate-200 pb-2 dark:border-slate-700">
            <div class="text-xs uppercase text-slate-500">References</div>
            <div class="text-lg font-semibold">{{ counts.references }}</div>
          </div>
          <div class="border-b border-slate-200 pb-2 dark:border-slate-700">
            <div class="text-xs uppercase text-slate-500">Quotes</div>
            <div class="text-lg font-semibold">{{ counts.quotes }}</div>
          </div>
          <div class="border-b border-slate-200 pb-2 dark:border-slate-700">
            <div class="text-xs uppercase text-slate-500">Purchase Orders</div>
            <div class="text-lg font-semibold">
              {{ counts.purchase_orders }}
            </div>
          </div>
          <div class="border-b border-slate-200 pb-2 dark:border-slate-700">
            <div class="text-xs uppercase text-slate-500">Contacts</div>
            <div class="text-lg font-semibold">{{ counts.contacts }}</div>
          </div>
          <div class="border-b border-slate-200 pb-2 dark:border-slate-700">
            <div class="text-xs uppercase text-slate-500">Addresses</div>
            <div class="text-lg font-semibold">{{ counts.addresses }}</div>
          </div>
        </div>

        <div
          v-if="totalAssets === 0"
          class="rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
        >
          No linked assets were found for this source customer.
        </div>

        <section
          v-if="assets.references.length"
          class="border-t border-slate-200 pt-4 dark:border-slate-700"
        >
          <h3 class="mb-2 text-sm font-semibold uppercase text-slate-500">
            References and Jobs
          </h3>
          <div class="overflow-x-auto">
            <table class="list">
              <thead>
                <tr>
                  <th class="px-3 py-2">Reference</th>
                  <th class="px-3 py-2">Name</th>
                  <th class="px-3 py-2">Type</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in assets.references" :key="item.id">
                  <td class="px-3 py-2 font-semibold">{{ item.reference }}</td>
                  <td class="px-3 py-2">{{ item.name }}</td>
                  <td class="px-3 py-2">
                    {{ item.type ?? "Reference" }}
                    <span v-if="item.deleted" class="ml-2 text-xs text-rose-500"
                      >Deleted</span
                    >
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section
          v-if="assets.quotes.length"
          class="border-t border-slate-200 pt-4 dark:border-slate-700"
        >
          <h3 class="mb-2 text-sm font-semibold uppercase text-slate-500">
            Quotes
          </h3>
          <div class="overflow-x-auto">
            <table class="list">
              <thead>
                <tr>
                  <th class="px-3 py-2">Quote</th>
                  <th class="px-3 py-2">Reference</th>
                  <th class="px-3 py-2">Status</th>
                  <th class="px-3 py-2">Recipient</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in assets.quotes" :key="item.id">
                  <td class="px-3 py-2 font-semibold">
                    #{{ item.id }} {{ item.description }}
                  </td>
                  <td class="px-3 py-2">
                    {{ item.reference ?? item.reference_type ?? "-" }}
                  </td>
                  <td class="px-3 py-2">
                    {{ item.status }}
                    <span v-if="item.deleted" class="ml-2 text-xs text-rose-500"
                      >Deleted</span
                    >
                  </td>
                  <td class="px-3 py-2">{{ item.recipient ?? "-" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section
          v-if="assets.purchase_orders.length"
          class="border-t border-slate-200 pt-4 dark:border-slate-700"
        >
          <h3 class="mb-2 text-sm font-semibold uppercase text-slate-500">
            Purchase Orders
          </h3>
          <div class="overflow-x-auto">
            <table class="list">
              <thead>
                <tr>
                  <th class="px-3 py-2">PO Number</th>
                  <th class="px-3 py-2">Status</th>
                  <th class="px-3 py-2">Invoices</th>
                  <th class="px-3 py-2">Description</th>
                  <th class="px-3 py-2">Merge Result</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in assets.purchase_orders" :key="item.id">
                  <td class="px-3 py-2 font-semibold">{{ item.po_number }}</td>
                  <td class="px-3 py-2">{{ item.status }}</td>
                  <td class="px-3 py-2">{{ item.invoice_count }}</td>
                  <td class="px-3 py-2">{{ item.description ?? "-" }}</td>
                  <td class="px-3 py-2">
                    {{
                      item.will_merge_into_existing
                        ? "Combine with destination PO"
                        : "Move to destination"
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section
          v-if="assets.contacts.length"
          class="border-t border-slate-200 pt-4 dark:border-slate-700"
        >
          <h3 class="mb-2 text-sm font-semibold uppercase text-slate-500">
            Contacts
          </h3>
          <div class="overflow-x-auto">
            <table class="list">
              <thead>
                <tr>
                  <th class="px-3 py-2">Name</th>
                  <th class="px-3 py-2">Email</th>
                  <th class="px-3 py-2">Phone</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in assets.contacts" :key="item.id">
                  <td class="px-3 py-2 font-semibold">
                    {{ item.name }}
                    <span
                      v-if="item.primary"
                      class="ml-2 text-xs text-slate-500"
                      >Primary</span
                    >
                  </td>
                  <td class="px-3 py-2">{{ item.email ?? "-" }}</td>
                  <td class="px-3 py-2">{{ item.phone ?? "-" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section
          v-if="assets.addresses.length"
          class="border-t border-slate-200 pt-4 dark:border-slate-700"
        >
          <h3 class="mb-2 text-sm font-semibold uppercase text-slate-500">
            Addresses
          </h3>
          <div class="overflow-x-auto">
            <table class="list">
              <thead>
                <tr>
                  <th class="px-3 py-2">Address</th>
                  <th class="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in assets.addresses" :key="item.id">
                  <td class="px-3 py-2">{{ item.data }}</td>
                  <td class="px-3 py-2">
                    {{ item.primary ? "Primary" : "-" }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </form-box>

    <form-box header="Confirm Merge">
      <div class="space-y-4">
        <label
          class="flex items-start gap-3 normal-case tracking-normal text-sm font-normal"
        >
          <input v-model="acknowledged" type="checkbox" class="mt-1" />
          <span>
            I understand that
            {{ source?.company ?? "the source customer" }} will be deleted and
            its listed assets will be owned by
            {{ destination?.company ?? "the destination customer" }}.
          </span>
        </label>

        <div>
          <label>Type MERGE</label>
          <input
            v-model="confirmationText"
            type="text"
            class="input w-full md:w-64"
            autocomplete="off"
            :disabled="!destination"
          />
        </div>

        <div class="flex flex-wrap gap-2">
          <button
            class="btn cancel disabled:cursor-not-allowed disabled:opacity-50"
            type="button"
            :disabled="!canMerge"
            @click.prevent="mergeCustomers"
          >
            {{ merging ? "Merging..." : "Merge Customers" }}
          </button>
          <router-link
            v-if="source"
            :to="{ name: 'customer-view', params: { id: source.id } }"
            class="btn"
          >
            Cancel
          </router-link>
        </div>
      </div>
    </form-box>
  </div>
</template>
